import { AppFunctionComponent, TextBuilder } from "../types"
import { graphql } from "gatsby"
import React, { createContext, useEffect, useState } from "react"
import { FootnotesSection } from "../components/common/footnotes.component"
import { embeddedEntryMaker } from "../components/community/blog/blog-post-rich-text-options"
import { Footnote } from "../components/community/blog/blog.interface"
import Layout from "../components/layout"
import SEO from "../components/seo.component"
import PageMetadata from "../components/seo/page-metadata.interface"
import { DetectedBreadcrumbs, TopNavigationWrapper } from "../navigation"
import HeroSectionResolver from "../resolvers/hero-section/hero-section.resolver"
import SectionResolver from "../resolvers/section.resolver"
import { BuilderSection, HeroBuilder } from "../types"
import { Menu } from "../navigation/types/header.interface"
import {
  KICKSTARTER_PAGE_PATH,
  SLEEP_BETTER_PAGE_PATH,
  WORK_FROM_HOME_PAGE_PATH,
} from "../constants/kickstarter"
import { MouseProvider } from "../hooks/use-mouse.hook"

interface Props {
  data: {
    page: {
      id: string
      path: string
      title: string
      darkMode: boolean
      root: boolean
      noIndex?: boolean
      meta: PageMetadata
      hero: HeroBuilder
      sections: BuilderSection[]
    }
    topNavigationContent: Menu
    footerNavigationContent: Menu
    promobarContent: TextBuilder
  }
}

interface StandardPageProps {
  landing?: boolean
  hero?: HeroBuilder
  sections?: BuilderSection[]
}

export const StandardPageResolver: AppFunctionComponent<StandardPageProps> = ({
  landing,
  hero,
  sections,
}) => {
  return (
    <>
      <TopNavigationWrapper
        landing={landing}
        breadcrumbsMenu={<DetectedBreadcrumbs />}
      />
      {hero && (
        // TODO: Final solution for submenus will be resolved in task: [MW2-220] Mechanism for Sitebuilder navigation
        <HeroSectionResolver {...hero} />
      )}
      {sections?.map((section: BuilderSection) => (
        <SectionResolver key={section.id} {...section} />
      ))}
    </>
  )
}

export const FootnoteContext = createContext({})

const PageTemplate: AppFunctionComponent<Props> = ({
  data: {
    page,
    topNavigationContent: { navigationItems: topNavigationItems },
    footerNavigationContent: { navigationItems: footerNavigationItems },
    promobarContent,
  },
}) => {
  const landing =
    page.path.includes(KICKSTARTER_PAGE_PATH) ||
    page.path.includes(WORK_FROM_HOME_PAGE_PATH)

  const isSleepBetter = page.path.includes(SLEEP_BETTER_PAGE_PATH)

  const footnotes: Footnote[] = []
  const footnotesGenerator = embeddedEntryMaker(footnotes)
  const { hero } = page
  const [footnotesExist, setFootnotesExist] = useState(false)

  useEffect(() => {
    setFootnotesExist(footnotes.length > 0)
  }, [footnotes.length])

  return (
    <FootnoteContext.Provider value={footnotesGenerator}>
      <MouseProvider>
        <Layout
          promobarContent={promobarContent}
          topNavigationItems={topNavigationItems}
          footerNavigationItems={footerNavigationItems}
          landing={landing}
          darkMode={page.darkMode}
          isSleepBetter={isSleepBetter}
        >
          <SEO {...page.meta} noIndex={page.noIndex} />
          <StandardPageResolver
            landing={landing}
            hero={hero}
            sections={page.sections}
          />
          {footnotesExist && (
            <FootnotesSection footnotes={footnotes} cols={2} />
          )}
        </Layout>
      </MouseProvider>
    </FootnoteContext.Provider>
  )
}

export default PageTemplate

export const query = graphql`
  query Page($id: String!, $language: String!, $locale: String!) {
    page: contentfulPageBuilder(id: { eq: $id }) {
      id
      title
      path
      root
      darkMode
      noIndex
      meta: metaTags {
        ...PageMeta
      }
      hero {
        ...HeroBuilder
        ...Slider
      }
      sections {
        ...Text
        ...TextWithEmbed
        ...List
        ...NewsletterSection
        ...Slider
        ...CustomComponent
      }
    }
    topNavigationContent: contentfulNavigation(
      contentfulid: { eq: "top-navigation" }
      node_locale: { eq: $locale }
    ) {
      ...Navigation
      node_locale
    }
    footerNavigationContent: contentfulNavigation(
      contentfulid: { eq: "footer-navigation" }
      node_locale: { eq: $locale }
    ) {
      ...Navigation
      node_locale
    }
    # Temporary, only for homepage slider. Depends on client localization.
    polishHeroHomeSlider: contentfulSlider(
      id: { eq: "1ebf76c2-d7e8-501e-8e4f-65017e89f42c" }
    ) {
      ...Slider
    }
    promobarContent: contentfulText(
      contentful_id: { eq: "67Y5XVRqB0r7iJbO4N8zxx" }
      node_locale: { eq: $locale }
    ) {
      text {
        raw
      }
      node_locale
    }
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`
